import { Icon } from '@iconify/react'
import { useTheme } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import type { ReactNode } from 'react'

import { MvTypography } from '@/components/atoms/mv-typography'

export interface PropsAlert {
  icon?: string
  title: string
  content?: ReactNode | string
  variant?: 'outlined' | 'contained'
  color?: 'primary' | 'info' | 'danger' | 'warning' | 'success'
  size?: 'small' | 'large'
  action?: ReactNode
  fullWidth?: boolean
  contentColor?: string
  accented?: boolean
  onClose?: () => void
}
export const Alert = ({
  icon,
  title,
  variant = 'contained',
  color = 'success',
  size = 'large',
  content,
  action,
  fullWidth = false,
  contentColor,
  accented = false,
  onClose
}: PropsAlert) => {
  const variantUsed = variant ?? 'contained'
  const tokenColor = color ?? 'success'
  const theme = useTheme()
  const foreground = theme.colorToken.text[tokenColor].boldest
  const background = theme.colorToken.background[tokenColor].subtlest
  const border = theme.colorToken.border[tokenColor]

  return (
    <Stack
      py='12px'
      px='16px'
      mb='12px'
      borderRadius='10px'
      direction='row'
      alignItems='flex-start'
      color={foreground}
      bgcolor={variantUsed == 'outlined' ? 'transparent' : background}
      spacing='12px'
      border='1px solid'
      borderColor={variantUsed == 'outlined' ? border.normal : border.subtle}
      borderLeft={accented ? `4px solid ${border.normal}` : undefined}
      width={fullWidth ? '100%' : 'auto'}
      role='alert'
    >
      {icon && (
        <Stack component='span' flexShrink={0} height='20px' alignItems='center' justifyContent='center' lineHeight={1}>
          <Icon icon={icon} fontSize='15px' />
        </Stack>
      )}
      <Stack spacing='2px' flexGrow={1} minWidth={0}>
        <MvTypography
          component='div'
          typeSize='PX'
          size={size == 'small' ? 'LABEL_MD_BOLDEST' : 'BODY_MD_BOLDEST'}
          color={foreground}
        >
          {title}
        </MvTypography>
        {typeof content == 'string' && content != '' ? (
          <MvTypography
            component='div'
            typeSize='PX'
            size={size == 'small' ? 'LABEL_SM_NORMAL' : 'BODY_MD_NORMAL'}
            color={contentColor ?? foreground}
            sx={size == 'small' ? undefined : { fontSize: '13px' }}
          >
            {content}
          </MvTypography>
        ) : (
          <>{content}</>
        )}
      </Stack>
      {action && <Stack alignSelf='center'>{action}</Stack>}
      {onClose && (
        <IconButton
          sx={{ color: 'inherit', opacity: 0.5, p: 0, mt: 0, lineHeight: 1 }}
          size='small'
          color='inherit'
          aria-label='close'
          onClick={onClose}
        >
          <Icon strokeWidth={2.5} icon='tabler:x' fontSize='15px' fontWeight={700} />
        </IconButton>
      )}
    </Stack>
  )
}
